import React, { Component } from "react";
import PropTypes from "prop-types";

// histogram renamed to bin https://observablehq.com/@d3/d3v6-migration-guide
import { bin, extent, max } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear, scaleTime } from "d3-scale";
import { select } from "d3-selection";
import { timeMonth } from "d3-time";

class CastDistChart extends Component {
  constructor(props) {
    super(props);
    this.createHistogram = this.createHistogram.bind(this);
    this.state = {
      tooltipDate: null,
      tooltipCasts: null,
      tooltipX: 0,
      tooltipY: 0,
      showTooltip: false
    };
  }

  componentDidMount() {
    this.createHistogram();
  }

  componentDidUpdate(prevProps) {
    // not great practice to manitpulate updates like this but in our case data will never update
    const currentExtent = extent(this.props.data, d => d.time);
    const prevExtent = extent(prevProps.data, d => d.time);
    if (
      prevProps.width !== this.props.width ||
      prevProps.height !== this.props.height ||
      // if the beginning or end of the extent has changed, update! much faster than comparing entire array
      currentExtent[0] !== prevExtent[0] ||
      currentExtent[1] !== prevExtent[1]
    ) {
      const { node } = this;
      select(node)
        .selectAll("g")
        .remove();
      this.createHistogram();
    }
  }

  createHistogram() {
    const { node } = this;
    const { height, width, margin } = this.props;

    // Make histogram take up 1/3 of space, depth chart 2/3
    const histHeight = (height - margin * 3) / 3;
    const depthHeight = height - histHeight - 2 * margin;
    const chartWidth = width - margin * 2;

    // define svg elements for each chart
    const depthChart = select(node)
      .append("g")
      .attr("class", "casts")
      .attr("transform", `translate(${margin},${margin + histHeight})`);

    const histChart = select(node)
      .append("g")
      .attr("class", "hist")
      .attr("transform", `translate(${margin},${margin})`);

    // Create scales (both charts share time scale)
    const maxDepth = max(this.props.data, d => d.depth);
    const timeExtent = extent(this.props.data, d => d.time);
    const xScale = scaleTime()
      .domain(timeExtent)
      .range([0, chartWidth]);
    const yDepthScale = scaleLinear()
      .domain([0, maxDepth])
      .range([0, depthHeight]);
    const yHistScale = scaleLinear().range([histHeight, 0]);

    // HISTOGRAM

    // set the parameters for the histogram
    const buildHistogram = bin()
      .value(d => d.time)
      .domain(xScale.domain())
      .thresholds(xScale.ticks(timeMonth));

    const bins = buildHistogram(this.props.data);
    yHistScale.domain([0, max(bins, d => d.length)]);

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sept",
      "Oct",
      "Nov",
      "Dec"
    ];

    // append the bar rectangles to the svg element
    select("g.hist")
      .selectAll("rect")
      .data(bins)
      .enter()
      .append("rect")
      .attr("class", "bar");

    select("g.hist")
      .selectAll("rect")
      .data(bins)
      .exit()
      .remove();

    select("g.hist")
      .selectAll("rect")
      .data(bins)
      .attr("x", 1)
      .attr(
        "transform",
        d => `translate(${xScale(d.x0)},${yHistScale(d.length)})`
      )
      .attr("width", d => xScale(d.x1) - xScale(d.x0) - 1)
      .attr("height", d => histHeight - yHistScale(d.length))
      .style("stroke", "#F6F0ED")
      .style("fill", "#66adc9")
      .on("mouseover", d => {
        const date = `${monthNames[new Date(d.x0).getMonth()]} ${new Date(
          d.x0
        ).getFullYear()}`;
        this.setState({
          tooltipCasts: d.length,
          tooltipDate: date,
          tooltipX: xScale(d.x1),
          tooltipY: histHeight - (1 - yHistScale(d.length)) - 20,
          showTooltip: true
        });
      })
      .on("mouseout", () => {
        this.setState({
          tooltipDate: null,
          tooltipCasts: null,
          showTooltip: false
        });
      });

    // add the y Axis with label
    histChart.append("g").call(axisLeft(yHistScale));
    histChart
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin)
      .attr("x", 0 - histHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-family", "sans-serif")
      .text("Casts per month");

    // DEPTH CHART

    // Add axes
    const xAxis = axisBottom().scale(xScale);
    const yAxis = axisLeft().scale(yDepthScale);

    depthChart
      .append("g")
      .attr("id", "xAxisG")
      .attr("transform", `translate(0,${depthHeight})`)
      .call(xAxis);
    depthChart
      .append("g")
      .attr("id", "yAxisG")
      .call(yAxis);
    depthChart
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin)
      .attr("x", 0 - depthHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-family", "sans-serif")
      .text("Depth");

    // append the bar rectangles to the svg element
    select("g.casts")
      .selectAll("g.line")
      .data(this.props.data)
      .enter()
      .append("g")
      .attr("class", "line");

    select("g.casts")
      .selectAll("g.line")
      .data(this.props.data)
      .exit()
      .remove();

    select("g.casts")
      .selectAll("g.line")
      .data(this.props.data)
      .attr("transform", d => {
        return `translate(${xScale(d.time)},${0})`;
      })
      // eslint-disable-next-line func-names
      .each(function(d) {
        select(this)
          .append("line")
          .attr("class", "depthline")
          .attr("x1", 0)
          .attr("x2", 0)
          .attr("y1", 0)
          .attr("y2", yDepthScale(d.depth))
          .style("stroke", "#28536B")
          .style("stroke-width", "2px");
      });
  }

  render() {
    return (
      <span>
        <svg
          ref={node => (this.node = node)}
          width={this.props.width}
          height={this.props.height}
        />
        <div
          style={{
            width: 80,
            height: 35,
            padding: 4,
            background: "#FFAD05",
            opacity: this.state.showTooltip ? 0.8 : 0,
            position: "absolute",
            left: this.state.tooltipX,
            top: this.state.tooltipY,
            fontFamily: "sans-serif",
            textAlign: "center",
            whiteSpace: "pre",
            borderRadius: "8px"
          }}
        >
          {this.state.tooltipDate}
          <br />
          {this.state.tooltipCasts} casts
        </div>
      </span>
    );
  }
}

CastDistChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  margin: PropTypes.number.isRequired
};

CastDistChart.defaultProps = {};

export default CastDistChart;
